import { AfterContentInit, Component, ContentChildren, forwardRef, Inject, Input, OnInit, QueryList, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { Moment } from 'moment';
import { Observable, of } from 'rxjs';
import { GET_DATA_ERROR } from '../../../common/constants';
import { isEmpty } from '../../../common/helper';
import { DynamicListColumn } from '../../../dashboard-area/dynamic-list/dynamic-list-column';
import { WorkSessionService } from '../../../dashboard-area/shared/work-session.service';
import { WorkSessionDetailsPageDialogComponent } from '../../../dashboard-area/work-session-details/work-session-details-page-dialog.component';
import { AlertWorkSession, Customer, Location as Loc, Thing, ThingDefinition, WorkSession } from '../../../model/index';
import { AppService } from '../../../service/app.service';
import { CustomPropertyService } from '../../../service/custom-property.service';
import { DateRangeService } from '../../../service/date-range.service';
import { NavigationService } from '../../../service/navigation.service';
import { ThingDefinitionService } from '../../../service/thing-definition.service';
import { SimpleSearchComponent } from '../../../shared/advanced-search/simple-search/simple-search.component';
import { AbstractThingContextService } from '../../../shared/class/abstract-thing-context-service.class';
import { CompositePartComponent, CompositePartMode } from '../../../shared/component';
import { PropertyComponent } from '../../../shared/component/property/property.component';
import { FormEditorComponent } from '../../../shared/form-editor/form-editor.component';
import { FormOption } from '../../../shared/form-editor/form-field-type/form-option.interface';
import { DetailsModeType } from '../../../shared/list-widget-v2/list-widget-v2.components';
import { DurationFormatterPipe } from '../../../shared/pipe';
import { COMPONENT_DEFINITION_REF } from "../../../shared/utility/component-definition-token";
import { DatetimeHelper } from '../../../shared/utility/datetime-helper';
import { ErrorUtility } from '../../../utility/error-utility';
import { AlertWorkSessionList } from '../../shared/alert-work-session-list';

@Component({
    selector: 'historical-work-session-list-widget',
    template: require('./historical-work-session-list-widget.component.html'),
    styles: [require('./historical-work-session-list-widget.component.css')],
    providers: [WorkSessionService, DurationFormatterPipe]
})
export class HistoricalWorkSessionWidgetComponent extends AlertWorkSessionList implements OnInit, AfterContentInit {

    @Input() title: string;

    @Input() showHeader: boolean;

    @Input() exportEnabled: boolean = true;

    @Input() expandable: boolean = true;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columns: QueryList<PropertyComponent | CompositePartComponent>;

    @ViewChild('advancedSearchEditor') private advancedSearchEditor: FormEditorComponent;

    @ViewChild('advancedSearchBarEditor') private advancedSearchBarEditor: FormEditorComponent;

    @ViewChild(SimpleSearchComponent) protected simpleSearch: SimpleSearchComponent;

    private contextData: { customer: Customer, location: Loc, thing: Thing };
    private isAdvancedSearchInitialized: boolean;

    static DEFAULT_COLUMN_NAMES = ['name', 'title', 'description', 'date', 'duration'];

    advancedSearchConfiguration: any[];
    advancedSearchBarConfiguration: any[];
    showAdvancedSearch: boolean = false;
    range: DateRange<Moment>;
    error: string;
    workSessionDefinitionGroups: { groupId: string, groupLabel: string, groupValues: FormOption[] }[] = [];

    constructor(
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => NavigationService)) navigationService: NavigationService,
        @Inject(forwardRef(() => CustomPropertyService)) customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => DurationFormatterPipe)) private durationFormatterPipe: DurationFormatterPipe,
        @Inject(forwardRef(() => ActivatedRoute)) private activatedRoute: ActivatedRoute,
        @Inject(forwardRef(() => WorkSessionService)) private workSessionService: WorkSessionService,
        @Inject(forwardRef(() => ThingDefinitionService)) private thingDefinitionService: ThingDefinitionService,
        @Inject(forwardRef(() => DateRangeService)) protected dateRangeService: DateRangeService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => MatDialog)) protected dialog: MatDialog
    ) {
        super(appService, navigationService, customPropertyService, dateRangeService, dialog);
    }

    ngOnInit(): void {
        this.title = this.title || 'completedWorkSessionsTitle';
        if (this.showHeader === undefined) {
            this.showHeader = true;
        }
        if (this.defaultPeriodValue) {
            var value = this.allowedPeriods.find(el => el.name == this.defaultPeriodValue);
            this.range = new DateRange(value.range.start, value.range.end);
        }
        this.init('completedWorkSessionsEmptyMessage');
    }

    ngAfterContentInit(): void {
        this.tableColumns = this.getTableColumns(this.columns, this.getDefaultColumnNames(), WorkSession);
        this.activatedRoute.data.subscribe(data => {
            this.contextData = data as any;
            if (this.advancedSearchAlwaysOpen) {
                this.showHideAdvancedSearch();
            } else {
                this.loadData(true);
            }
        });
    }

    getDefaultColumnNames(): string[] {
        return HistoricalWorkSessionWidgetComponent.DEFAULT_COLUMN_NAMES;
    }

    loadNext(): void {
        this.infiniteScrollLoading = true;
        let searchText = this.searchKey;
        let startDate, endDate, workSessionDefinitionIds, thingDefinitionIds;
        if (this.advancedSearchEditor != undefined) {
            const body = this.advancedSearchEditor.getObjectValue();
            if (body != undefined) {
                startDate = DatetimeHelper.toMillisString(_.get(body, "startDate"));
                endDate = DatetimeHelper.toMillisString(_.get(body, "endDate"));
                workSessionDefinitionIds = _.get(body, "workSessionDefinitionIds");
                thingDefinitionIds = workSessionDefinitionIds && workSessionDefinitionIds.length ? null : _.get(body, "thingDefinitionIds");
            }
        }
        if (this.range) {
            startDate = this.getStartDate(this.range);
            endDate = this.getEndDate(this.range);
        }
        if (this.advancedSearchBarEditor != undefined) {
            const searchBarBody = this.advancedSearchBarEditor.getObjectValue();
            if (searchBarBody != undefined) {
                searchText = _.get(searchBarBody, "key");
            }
        }
        this.loadData(false, searchText, startDate, endDate, workSessionDefinitionIds, thingDefinitionIds).then(() => {
            this.infiniteScrollLoading = false;
        });
    }

    showHideAdvancedSearch(): void {
        if (this.showAdvancedSearch) {
            this.showAdvancedSearch = false;
            this.completeShowHideAdvancedSearch();
        } else {
            if (this.simpleSearch) {
                this.simpleSearch.reset();
            }
            if (this.isAdvancedSearchInitialized) {
                this.completeShowHideAdvancedSearch();
                this.showAdvancedSearch = true;
            } else {
                this.isAdvancedSearchInitialized = true;
                this.initializeAdvancedSearch().then(() => {
                    this.completeShowHideAdvancedSearch();
                    this.showAdvancedSearch = true;
                });
            }
        }
    }

    private completeShowHideAdvancedSearch(): void {
        this.range = null;
        this.loaded = false;
        this.loadData(true);
    }

    private initializeAdvancedSearch(): Promise<void> {
        let promises = [];
        promises.push(this.workSessionService.getWorkSessionDefinitionTypes(this.contextData?.thing));
        promises.push(this.thingDefinitionService.getThingDefinitions());
        return Promise.all(promises).then(result => {
            const advancedSearchBarConfiguration = [
                { name: 'key', type: 'SEARCH', value: this.searchKey },
            ]
            let advancedSearchConfiguration: any = [
                { name: 'startDate', label: 'Start Date', type: 'DATE' },
                { name: 'endDate', label: 'End Date', type: 'DATE' }
            ];
            const thingDefinitions = result[1].map((thingDef: ThingDefinition) => { return { value: thingDef.id, label: thingDef.name }; });
            if (thingDefinitions && thingDefinitions.length && !this.thingContextService.getCurrentThing()) {
                advancedSearchConfiguration.push({ name: 'thingDefinitionIds', label: 'thingDefinitionsTabItem', type: 'STRING', selectionMode: 'MAT_SELECTION', values: thingDefinitions, multipleSelection: true });
            }
            if (result[0] && result[0].length) {
                this.workSessionDefinitionGroups = result[0].map(el => { return { groupId: el.id, groupLabel: el.label, groupValues: el.children.map(child => { return { value: child.id, label: child.label } }) } });
            }
            if (this.workSessionDefinitionGroups && this.workSessionDefinitionGroups.length) {
                advancedSearchConfiguration.push({ name: 'workSessionDefinitionIds', label: 'workSessionDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: this.workSessionDefinitionGroups });
            }
            this.advancedSearchBarConfiguration = advancedSearchBarConfiguration;
            this.advancedSearchConfiguration = advancedSearchConfiguration;
        });
    }

    search(searchBody: any, firstPage: boolean) {
        this.loaded = false;
        this.searchKey = searchBody.key;
        if (this.range) {
            this.loadData(firstPage, searchBody.key, this.getStartDate(this.range), this.getEndDate(this.range));
        } else {
            this.loadData(firstPage, searchBody.key);
        }
    }

    advancedSearch(firstPage: boolean, $event) {
        const eventObject = $event.currentTarget;
        this.loaded = false;
        const body = this.advancedSearchEditor.getObjectValue();
        const searchBarBody = this.advancedSearchBarEditor.getObjectValue();
        if (body != undefined) {
            const startDate = DatetimeHelper.toMillisString(_.get(body, "startDate"));
            const endDate = DatetimeHelper.toMillisString(_.get(body, "endDate"));
            const workSessionDefinitionIds = _.get(body, "workSessionDefinitionIds");
            const thingDefinitionIds = workSessionDefinitionIds && workSessionDefinitionIds.length ? null : _.get(body, "thingDefinitionIds");
            let key = null;
            if (searchBarBody != undefined) {
                key = _.get(searchBarBody, "key");
            }
            this.loadData(firstPage, key, startDate, endDate, workSessionDefinitionIds, thingDefinitionIds).then(
                () => eventObject.blur()
            );
        }
    }

    clearAll($event) {
        const eventObject = $event.currentTarget;
        this.advancedSearchEditor.reset();
        this.advancedSearchBarEditor.reset();
        this.loaded = false;
        this.loadData(true).then(
            () => eventObject.blur()
        );
    }

    infiniteScrollMobile($event): void {
        const el = <any>($event.srcElement || $event.target);
        if (el.scrollHeight - el.scrollTop === el.clientHeight) {
            this.loadNext();
        }
    }

    getValue(column: DynamicListColumn, workSession: WorkSession): Observable<any> {
        let path = '';
        const columnName = column.name;

        if (column.compositePart) {
            return column.compositePart.get(workSession, CompositePartMode.LIST);
        }

        let defaultValue = this.getDefaultPropertyValue(columnName) || '';
        let valueMap = this.getValueMap(columnName);
        if (columnName.startsWith('thing.') || columnName.startsWith('thingDefinition.') || columnName.startsWith('location.') || columnName.startsWith('customer.')) {
            path = columnName;
        } else {
            path = column.path;
        }
        let val = path === null ? workSession : _.get(workSession, path, defaultValue);
        if (valueMap && !isEmpty(val) && !isEmpty(valueMap[val])) {
            val = valueMap[val];
        }
        return of(val);
    }

    private loadData(firstPage: boolean, searchText?: string, startDate?: string, endDate?: string, workSessionDefinitonIds?: string[], thingDefinitionIds?: string[]): Promise<void> {
        this.error = null;
        let columnNames = this.tableColumns.filter(col => !col.compositePart).map(col => col.name);
        this.tableColumns.filter(col => col.compositePart).forEach(
            col => {
                if (col.compositePart.properties) {
                    col.compositePart.properties.toArray().forEach(prop => {
                        columnNames.push(prop.name);
                    });
                }
            }
        );
        return this.workSessionService.loadHistoricalWorkSession(columnNames, firstPage, this.contextData, searchText, startDate, endDate, workSessionDefinitonIds, thingDefinitionIds)
            .then(workSessions => {
                this.alertWorkSessions = workSessions;
                this.computeInfoColumn();
                this.tableData = this.getTableData();
                this.loaded = true;
            })
            .catch(err => {
                this.error = ErrorUtility.getMessage(err, GET_DATA_ERROR);
                this.loaded = true;
            });
    }

    private computeInfoColumn(): void {
        if (this.alertWorkSessions.some(ws => (ws as WorkSession).templateName)) {
            this.showDetailsColumn = true;
        }
    }

    selectPeriod(event: { range: DateRange<Moment>, searchKey: any }) {
        this.range = event.range;
        this.loadData(true, event.searchKey, this.getStartDate(event.range), this.getEndDate(event.range));
    }

    export(): void {
        this.workSessionService.exportHistoricalWorkSessions(this.contextData, this.searchKey, this.getStartDate(this.range),
            this.getEndDate(this.range), this.columnFieldNames, this.columnFieldLabels);
    }

    advancedExport(): void {
        const body = this.advancedSearchEditor.getObjectValue();
        const searchBarBody = this.advancedSearchBarEditor.getObjectValue();
        if (body != undefined) {
            const startDate = DatetimeHelper.toMillisString(_.get(body, "startDate"));
            const endDate = DatetimeHelper.toMillisString(_.get(body, "endDate"));
            const workSessionDefinitionIds = _.get(body, "workSessionDefinitionIds");
            const thingDefinitionIds = workSessionDefinitionIds && workSessionDefinitionIds.length ? null : _.get(body, "thingDefinitionIds");
            let key = null;
            if (searchBarBody != undefined) {
                key = _.get(searchBarBody, "key");
            }
            this.workSessionService.exportHistoricalWorkSessions(this.contextData, key, startDate, endDate, this.columnFieldNames, this.columnFieldLabels, workSessionDefinitionIds, thingDefinitionIds);
        }
    }

    getDuration(workSession: AlertWorkSession): string {
        return this.durationFormatterPipe.transform({ startTimestamp: workSession.startTimestamp, endTimestamp: workSession.endTimestamp });
    }

    isDetailsButtonVisible(alertWorkSession: WorkSession): boolean {
        return !!alertWorkSession.templateName;
    }

    goToWorkSessionDetails(id: string): void {
        if (this.detailsMode == DetailsModeType.PAGE) {
            this.navigationService.navigateTo(['/dashboard/historical_work_session_details', id]);
        } else if (this.detailsMode == DetailsModeType.POPUP) {
            this.openDialog(id);
        }
    }

    updateWorkSessionDefinitionGroup(name: string): void {
        if (name == 'thingDefinitionIds') {
            const rawValues = this.advancedSearchEditor.getObjectValue();
            const thingDefintionIds: string[] = _.get(rawValues, 'thingDefinitionIds');
            const configurations = _.cloneDeep(this.advancedSearchConfiguration);
            const groupValues = this.workSessionDefinitionGroups.filter(group => thingDefintionIds.includes(group.groupId));
            if (configurations.find(el => el.name == 'workSessionDefinitionIds')) {
                if (thingDefintionIds && thingDefintionIds.length) {
                    if (groupValues && groupValues.length) {
                        configurations.find(el => el.name == 'workSessionDefinitionIds').matSelectGroupValues = groupValues;
                    } else {
                        configurations.splice(configurations.indexOf(el => el.name == 'workSessionDefinitionIds'), 1);
                    }
                } else {
                    configurations.find(el => el.name == 'workSessionDefinitionIds').matSelectGroupValues = this.workSessionDefinitionGroups;
                }
            } else {
                if (thingDefintionIds && thingDefintionIds.length) {
                    if (groupValues && groupValues.length) {
                        configurations.push({ name: 'workSessionDefinitionIds', label: 'workSessionDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: groupValues });
                    }
                } else if (this.workSessionDefinitionGroups && this.workSessionDefinitionGroups.length) {
                    configurations.push({ name: 'workSessionDefinitionIds', label: 'workSessionDefinitionTypeProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: [], multipleSelection: true, enableMatSelectGroups: true, matSelectGroupValues: this.workSessionDefinitionGroups });
                }
            }
            configurations.forEach(conf => {
                if (conf.name != 'workSessionDefinitionIds') {
                    conf.value = rawValues[conf.name];
                }
            });
            this.advancedSearchConfiguration = configurations;
        }
    }

    private openDialog(id: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        dialogConfig.data = { id: id, isHistorical: true };
        this.dialog.open(WorkSessionDetailsPageDialogComponent, dialogConfig);
    }
}